import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';

export class TraderverseMarketWideInsiderActivityFlow {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;
  private startDate: string = '';
  private endDate: string = '';
  private selectedRange: string = 'Last 3 Months';
  private selectedCountry: string = 'All Countries';
  private selectedTradeType: string = 'All';
  private datePickerInstance: any = null;
  private filtersCloseHandler: ((e: MouseEvent) => void) | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });

    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private setDateRange(range: string): void {
    const today = new Date();
    let start = new Date();

    switch (range) {
      case 'Current Week':
        start.setDate(today.getDate() - today.getDay());
        break;
      case 'Last Week':
        start.setDate(today.getDate() - today.getDay() - 7);
        today.setDate(start.getDate() + 6);
        break;
      case 'Last 2 Weeks':
        start.setDate(today.getDate() - 14);
        break;
      case 'Last 3 Months':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'This Month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        start.setMonth(today.getMonth() - 1);
        break;
    }

    this.startDate = start.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.setDateRange(this.selectedRange);

    await this.createWidget();
    setupHeaderPin(this.shadowRoot, this.config);
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private async createWidget(): Promise<void> {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Market Wide Insider Activity Flow </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>

        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="item" data-testing-id="trade-type-selector">
              <select class="trade-type-select" data-testing-id="trade-type-select">
                <option value="All" selected>All</option>
                <option value="Buy">Buy</option>
                <option value="Sell">Sell</option>
              </select>
            </div>
            <span></span>

            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select class="range-select">
                  <option value="Current Week" >Current Week</option>
                  <option value="Last Week" >Last Week</option>
                  <option value="Last 2 Weeks">Last 2 Weeks</option>
                  <option value="Last 3 Months" selected>Last 3 Months</option>
                  <option value="This Month">This Month</option>
                </select>
              </div>
              <div class="item date-picker" data-testing-id="date-picker-container">
                <input type="text" class="date-input" data-testing-id="date-input" />
              </div>
            </div>
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; margin:0; padding:0;" data-testing-id="events-container"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    await this.attachStyles();

    const tradeTypeSelect = this.shadowRoot.querySelector('.trade-type-select') as HTMLSelectElement;
    const rangeSelect = this.shadowRoot.querySelector('.range-select') as HTMLSelectElement;

    tradeTypeSelect.addEventListener('change', () => {
      this.selectedTradeType = tradeTypeSelect.value;
      this.setupChart();
    });

    rangeSelect.addEventListener('change', () => {
      this.selectedRange = rangeSelect.value;
      this.setDateRange(this.selectedRange);
      this.setupChart();
      this.initDatePicker();
    });

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
      filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
      this.filtersCloseHandler = (e: MouseEvent) => {
        const target = e.target as Node;
        if (!filtersBox.classList.contains('hidden') &&
            !filtersBox.contains(target) && !filtersToggle.contains(target)) {
          filtersBox.classList.add('hidden');
        }
      };
      document.addEventListener('click', this.filtersCloseHandler);
    }

    setTimeout(() => this.initDatePicker(), 0);
  }

  private initDatePicker(): void {
    try {
      const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
      if (!input) return;

      if (this.datePickerInstance && this.datePickerInstance.destroy) {
        this.datePickerInstance.destroy();
        this.datePickerInstance = null;
      }

      this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        defaultDate: [this.startDate || new Date(), this.endDate || new Date()],
        onClose: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            this.startDate = format(start, 'yyyy-MM-dd');
            this.endDate = format(end, 'yyyy-MM-dd');
            this.setupChart();
          }
        },
      });
    } catch (err) {
      console.warn('Date picker init failed:', err);
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      script.async = true;
      script.onload = () => resolve(true);
      this.shadowRoot.appendChild(script);
    });
  }

  private async setupChart(): Promise<void> {
    try {
      let payload: any = {
        start_date: this.startDate,
        end_date: this.endDate
      };

      if (this.selectedTradeType === 'Buy' || this.selectedTradeType === 'Sell') {
        payload.transaction_status = this.selectedTradeType;
      }

      const response = await apiGet({
        method: "POST",
        url: `${APIURL}/v1/discover/latest_insider_trader`,
        body: payload,
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      const fromApi = (this.currentData as any)?.last_updated_at ?? (this.currentData as any)?.data?.[0]?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();

      this.updateDisplay();
      this.emit('dataUpdate', { symbol: this.config.symbol, data: this.currentData });
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private filterByCountry(selectedCountry: string): void {
    const events = this.normalizeEventsData(this.currentData);
    let filtered = events;
    if (selectedCountry !== 'All Countries') {
      filtered = filtered.filter((item: any) => item.country === selectedCountry);
    }
    this.renderFilteredEvents(filtered);
  }

  private normalizeEventsData(source: any): any[] {
    if (Array.isArray(source)) return source;
    if (!source || typeof source !== 'object') return [];

    if (Array.isArray(source.data)) return source.data;

    const nestedData = (source as any).data;
    if (nestedData && typeof nestedData === 'object') {
      if (Array.isArray(nestedData.data)) return nestedData.data;
      if (Array.isArray(nestedData.results)) return nestedData.results;
      if (Array.isArray(nestedData.items)) return nestedData.items;
      if (Array.isArray(nestedData.events)) return nestedData.events;
    }

    if (Array.isArray((source as any).results)) return (source as any).results;
    if (Array.isArray((source as any).items)) return (source as any).items;
    if (Array.isArray((source as any).events)) return (source as any).events;

    return [];
  }

  private renderFilteredEvents(eventsData: any): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    if (!container) return;

    const rows = this.normalizeEventsData(eventsData);

    if (!rows || rows.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    let html = `
      <div class="eventsMain latestInsiderTrader" data-testing-id="insider-activity-main">
        <div class="item header" data-testing-id="table-header">
          <p data-testing-id="header-date">Date</p>
          <p data-testing-id="header-symbol">Symbol</p>
          <p data-testing-id="header-key-members">Key Members</p>
          <p data-testing-id="header-shares">Shares</p>
          <p data-testing-id="header-buy-sell">Buy/Sell</p>
        </div>
    `;

    for (let idx = 0; idx < rows.length; idx++) {
      const event = rows[idx];
      const { date, logo, symbol, ownerName, transactionStatus, transaction_amount } = event;
      const transactionStatusClass = transactionStatus === 'Buy' ? 'up' : 'down';

      html += `
        <div class="item body" 
             data-testing-id="insider-activity-row-${idx}" 
             data-index="${idx}"
             data-symbol="${symbol || ''}" 
             data-transaction-status="${transactionStatus || ''}"
             data-transaction-amount="${transaction_amount || 0}">
          <p data-testing-id="cell-date">${date || '-'}</p>
          <p data-testing-id="cell-symbol">
            <img src="${logo || ''}" alt="${symbol}" style="width:24px;height:24px;border-radius:4px;" data-testing-id="symbol-img"/>
            ${symbol || '-'}
          </p>
          <p data-testing-id="cell-owner-name">${ownerName || '-'}</p>
          <p data-testing-id="cell-transaction-amount">$${transaction_amount ? transaction_amount.toLocaleString() : '-'}</p>
          <p class="stat ${transactionStatusClass}" data-testing-id="cell-transaction-status">
            <span data-testing-id="status-text">${transactionStatus || '-'}</span>
          </p>
        </div>
      `;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
      const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
      const rows = this.normalizeEventsData(this.currentData);

  if (chartDv) {
    if (rows && rows.length > 0) {
      chartDv.setAttribute(
        'data-testing-id',
        'market-wide-insider-activity-flow-data'
      );
    } else {
      chartDv.removeAttribute('data-testing-id');
    }
  }

    this.updateLastUpdatedTooltip();
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.filterByCountry(this.selectedCountry);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.filtersCloseHandler) {
      document.removeEventListener('click', this.filtersCloseHandler);
      this.filtersCloseHandler = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach(h => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseMarketWideInsiderActivityFlow: typeof TraderverseMarketWideInsiderActivityFlow;
  }
}
if (typeof window !== 'undefined') {
  window.TraderverseMarketWideInsiderActivityFlow = TraderverseMarketWideInsiderActivityFlow;
}